// AgingBalanceReport.jsx
import * as React from "react";
import {
  Box,
  Button,
  Card,
  Container,
  CssBaseline,
  Divider,
  Fade,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Typography,
  alpha,
  Checkbox
} from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import WarningIcon from "@mui/icons-material/Warning";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { DataGrid } from "@mui/x-data-grid";
import AppNavbar from "../../../../components/AppNavbar";
import Header from "../../../../components/Header";
import SideMenu from "../../../../components/SideMenu";
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../../../dashboard/theme/customizations';
import AppTheme from "../../../../shared-theme/AppTheme";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { formatNumberWithCommas } from "../../../../utils/generalFunctions";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const drawerWidth = 280;

const MetricCard = ({ label, value, icon, delay = 0 }) => (
    <Fade in timeout={800 + delay}>
    <Card sx={{ 
      p: 3, 
      minWidth: 280, 
      flex: 1,
        background: 'linear-gradient(135deg, #18586b 0%, #0f3a42 100%)',
      color: 'white',
      position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-8px) scale(1.02)',
          boxShadow: '0 20px 40px rgba(24, 88, 107, 0.3)',
          '& .metric-icon': {
            transform: 'scale(1.1) rotate(5deg)',
          },
          '& .metric-value': {
            transform: 'scale(1.05)',
          }
        }
    }}>
      <Box sx={{ position: 'relative', zIndex: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ opacity: 0.9 }}>
      {label}
    </Typography>
          {icon && (
              <Box className="metric-icon" sx={{ 
                p: 1.5, 
              borderRadius: '50%', 
              backgroundColor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.1)'
            }}>
              {icon}
            </Box>
          )}
        </Box>
          <Typography className="metric-value" variant="h4" fontWeight={800} sx={{ 
            mb: 1,
            transition: 'all 0.3s ease',
            background: 'linear-gradient(45deg, #ffffff, #e0f2f1)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
      {value}
        </Typography>
          
        
          
        
      </Box>
        
        {/* Animated background elements */}
      <Box sx={{
        position: 'absolute',
        top: -20,
        right: -20,
        width: 80,
        height: 80,
        borderRadius: '50%',
        backgroundColor: 'rgba(255,255,255,0.1)',
          zIndex: 1,
          animation: 'float 6s ease-in-out infinite'
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: -30,
          left: -30,
          width: 60,
          height: 60,
          borderRadius: '50%',
          backgroundColor: 'rgba(255,255,255,0.05)',
          zIndex: 1,
          animation: 'float 4s ease-in-out infinite reverse'
        }} />
        
        <style jsx>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-10px) rotate(180deg); }
          }
        `}</style>
    </Card>
    </Fade>
  );
  
export default function AntiguedadSaldoReport(props) {
  const navigate = useNavigate();
  // ---- Filters (controlled state) ----
  const [customer, setCustomer] = React.useState("");
  const [docType, setDocType] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [agingRange, setAgingRange] = React.useState("");
  const [prestamos, setPrestamos] = React.useState([]);
  const [prestamosIndefinidos, setPrestamosIndefinidos] = React.useState(false);
  const [showInterest, setShowInterest] = React.useState(true);
  const [showCapital, setShowCapital] = React.useState(true);
  const [showMora, setShowMora] = React.useState(true);
  // ---- Data (stubbed to match your HTML) ----
  const [rows, setRows] = React.useState([
    {
      id: 1,
      customer: "Tech Solutions Inc.",
      ref: "INV-2023-001",
      total: 15000,
      d0_30: 10000,
      d31_60: 3000,
      d61_90: 1500,
      d90p: 500,
    },
    {
      id: 2,
      customer: "Global Retail Ltd.",
      ref: "INV-2023-002",
      total: 8500,
      d0_30: 5000,
      d31_60: 2000,
      d61_90: 1000,
      d90p: 500,
    },
    {
      id: 3,
      customer: "Creative Designs Co.",
      ref: "INV-2023-003",
      total: 12000,
      d0_30: 8000,
      d31_60: 2500,
      d61_90: 1000,
      d90p: 500,
    },
    {
      id: 4,
      customer: "Innovative Systems LLC",
      ref: "INV-2023-004",
      total: 6000,
      d0_30: 4000,
      d31_60: 1000,
      d61_90: 500,
      d90p: 500,
    },
    {
      id: 5,
      customer: "Dynamic Marketing Group",
      ref: "INV-2023-005",
      total: 9500,
      d0_30: 6000,
      d31_60: 2000,
      d61_90: 1000,
      d90p: 500,
    },
  ]);

  // ---- Column visibility based on breakpoints ----
  const columnVisibilityModel = React.useMemo(
    () => ({
      // Show all columns by default - can be customized based on screen size if needed
      d90p: true,
      d61_90: true,
      d31_60: true,
      d0_30: true,
      total: true,
      ref: true,
      customer: true,
    }),
    []
  );

  const currency = (n) => {
    if (n === undefined || n === null || isNaN(n)) return '$0';
    return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  };

  const columns = [
    { field: "customer", headerName: "Nombre", flex: 1, minWidth: 280 },
    { field: "identificacion", headerName: "No. Prestamo", flex: 1, minWidth: 220},
    {
      field: "total",
      headerName: "Total Balance",
      minWidth: 160,
      valueFormatter: ({ value }) => formatNumberWithCommas(value || 0),
    },
    {
      field: "d0_30",
      headerName: "0–30 Days",
      minWidth: 140,
      headerClassName: "ag-col-0-30",
    },
    {
      field: "d31_60",
      headerName: "31–60 Days",
      minWidth: 140,
      headerClassName: "ag-col-31-60",
    },
    {
      field: "d61_90",
      headerName: "61–90 Days",
      minWidth: 140,
      headerClassName: "ag-col-61-90",
    },
    {
      field: "d90p",
      headerName: "+90 Days",
      minWidth: 140,
      valueFormatter: ({ value }) => formatNumberWithCommas(value || 0),
      headerClassName: "ag-col-90p",
    },
  ];

  // ---- Simple totals (footer-like summary) ----
  const totals = rows.reduce(
    (acc, r) => {
      acc.total += parseFloat(r.total) || 0;
      acc.d0_30 += parseFloat(r.d0_30) || 0;
      acc.d31_60 += parseFloat(r.d31_60) || 0;
      acc.d61_90 += parseFloat(r.d61_90) || 0;
      acc.d90p += parseFloat(r.d90p) || 0;
      return acc;
    },
    { total: 0, d0_30: 0, d31_60: 0, d61_90: 0, d90p: 0 }
  );
  // ---- Handlers for actions (stubs) ----
  const handleGenerate = async (showCapital,showInterest,showMora) => {
    // Validate that at least one option is selected
    if (!showInterest && !showCapital && !showMora) {
      alert('Por favor selecciona al menos una opción: Intereses, Capital o Mora');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/prestamos/reporte/antiguedad_saldo`,{
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        },
        body: JSON.stringify({
          fecha: new Date().toISOString().split('T')[0],
          incluir_intereses: showInterest,
          incluir_capital: showCapital,
          incluir_mora: showMora,
          idcompany: localStorage.getItem('idcompany')
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!Array.isArray(data)) {
        console.error('API response is not an array:', data);
        return;
      }
    
    // Create new rows array from API data
    const newRows = data
      .filter(item => item.max_days_overdue > 0) // Only include items with positive amounts
      .map(item => {
        var dias = item.max_days_overdue;
        var categoria = ""
        if(dias <= 30){
          categoria = "d0_30";
        }else if(dias <= 60){
          categoria = "d31_60";
        }else if(dias <= 90){
          categoria = "d61_90";
        }else{
          categoria = "d90p";
        }

        var total = 0
        if(showInterest){
            total = parseFloat(item.interest_overdue);
        }
        if(showCapital){
            total += parseFloat(item.capital_overdue);
        }
        if(showMora){
            total += parseFloat(item.mora_overdue) || 0;
        }

        return {
          id: item.id_prestamo,
          customer: item.cliente_nombre,
          identificacion: item.loan_id,
          total: parseFloat(total),
          capital: parseFloat(item.capital_overdue),
          interes: parseFloat(item.interest_overdue),
          mora: parseFloat(item.mora_overdue) || 0,
          d0_30: categoria === "d0_30" ? parseFloat(total) : 0,
          d31_60: categoria === "d31_60" ? parseFloat(total) : 0,
          d61_90: categoria === "d61_90" ? parseFloat(total) : 0,
          d90p: categoria === "d90p" ? parseFloat(total) : 0,
        };
      });

      // Update state with new data
      setRows(newRows);
      setPrestamos(newRows);
      
    } catch (error) {
      console.error('Error generating report:', error);
      alert(`Error al generar el reporte: ${error.message}`);
    }
  };

  const handleExportPDF = () => {
    if (rows.length === 0) {
      alert("No hay datos para exportar.");
      return;
    }

    try {
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const tableData = rows.map((row) => [
        row.customer || "",
        row.identificacion || "",
        formatNumberWithCommas((parseFloat(row.total) || 0).toFixed(2)),
        formatNumberWithCommas((parseFloat(row.d0_30) || 0).toFixed(2)),
        formatNumberWithCommas((parseFloat(row.d31_60) || 0).toFixed(2)),
        formatNumberWithCommas((parseFloat(row.d61_90) || 0).toFixed(2)),
        formatNumberWithCommas((parseFloat(row.d90p) || 0).toFixed(2)),
      ]);

      autoTable(doc, {
        startY: 14,
        head: [["Nombre", "No. Prestamo", "Total Balance", "0-30 Dias", "31-60 Dias", "61-90 Dias", "+90 Dias"]],
        body: tableData,
        theme: "striped",
        headStyles: { fillColor: [24, 88, 107], textColor: 255, fontStyle: "bold", fontSize: 8 },
        bodyStyles: { fontSize: 7 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        columnStyles: {
          0: { cellWidth: 55 },
          1: { cellWidth: 38 },
          2: { cellWidth: 32, halign: "right" },
          3: { cellWidth: 30, halign: "right" },
          4: { cellWidth: 30, halign: "right" },
          5: { cellWidth: 30, halign: "right" },
          6: { cellWidth: 30, halign: "right" },
        },
        margin: { left: 14, right: 14 },
        styles: { overflow: "linebreak", cellPadding: 2 },
      });

      const pageCount = doc.internal.pages.length - 1;
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont("helvetica", "italic");
        doc.text(
          `Página ${i} de ${pageCount}`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: "center" }
        );
      }

      const pdfBlob = doc.output("blob");
      const url = URL.createObjectURL(pdfBlob);
      window.open(url, "_blank");
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(`No se pudo generar el PDF: ${error.message}`);
    }
  };
  const handleExportExcel = () => {
    // TODO: implement Excel export
    console.log("Export Excel");
  };
  const handlePrint = () => window.print();

  // ---- Filter content ----
  const filterContent = (
    <Box>
      {/* Filter Controls Row */}
      <Stack 
        direction={{ xs: "column", sm: "row" }} 
        spacing={2} 
        sx={{ mb: 3 }}
        useFlexGap
      >
        {/* Checkbox Controls */}
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left', mb: 1 }}>
            Tipo de Calculo
          </Typography>
          <FormGroup sx={{display:"flex", flexDirection:"row", gap:2}}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showInterest}
                  onChange={(e) => {setShowInterest(e.target.checked); handleGenerate(showCapital,e.target.checked,showMora)}}
                  color="primary"
                />
              }
              label="Intereses"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={showCapital}
                  onChange={(e) => {setShowCapital(e.target.checked); handleGenerate(e.target.checked,showInterest,showMora)}}
                  color="primary"
                />
              }
              label="Capital"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={showMora}
                  onChange={(e) => {setShowMora(e.target.checked); handleGenerate(showCapital,showInterest,e.target.checked)}}
                  color="primary"
                />
              }
              label="Mora"
            />
          </FormGroup>
          {/* Selection indicator */}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0, display: 'block', textAlign: 'left' }}>
            {(() => {
              const seleccion = [
                showInterest && 'Intereses',
                showCapital && 'Capital',
                showMora && 'Mora'
              ].filter(Boolean);
              return seleccion.length > 0 ? `Mostrando: ${seleccion.join(' + ')}` : 'Selecciona una opción';
            })()}
      </Typography>
        </Box>

      </Stack>

      {/* Export Options Row */}
      <Stack 
        direction={{ xs: "column", sm: "row" }} 
        spacing={2} 
        alignItems="center"
        sx={{ 
          borderTop: 1, 
          borderColor: 'divider', 
          pt: 2,
          justifyContent: 'space-between'
        }}
      >
        <Stack direction="row" spacing={1}>
          <Button onClick={handleExportPDF} startIcon={<PictureAsPdfOutlinedIcon />} color="inherit" size="small">
            Exportar a PDF
          </Button>
          <Button onClick={handleExportExcel} startIcon={<GridOnOutlinedIcon />} color="inherit" size="small">
            Export to Excel
          </Button>
          <Button onClick={handlePrint} startIcon={<PrintOutlinedIcon />} color="inherit" size="small">
            Print Report
          </Button>
        </Stack>
      </Stack>
    </Box>
  );


  // Remove automatic generation on mount - let user click the button instead
   React.useEffect(() => {
     handleGenerate(showCapital,showInterest,showMora);
   }, []);
  
  

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        {/* Main content */}
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: 'auto',
          })}
        >
          <Stack
            spacing={2}
              sx={{
              alignItems: 'center',
              mx: 3,
              pb: 5,
              mt: { xs: 8, md: 0 },
            }}
          >
            <Header parent={"Gestión Comercial"} child={"Reportes"} />
            <Container maxWidth={false} sx={{ px: 5, py: 5 }}>
              <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', fontFamily: 'Inter, "Noto Sans", sans-serif' }}>
                <Box maxWidth="lg" mx="auto" py={4}>
                  <Box display="flex" px={2} mb={2}>
                    <Button variant="outlined" startIcon={<ArrowBack/>} onClick={()=>{navigate(-1)}}>Atras</Button>
                    <Box display={"flex"} flexDirection={"column"} gap={2} ml={5}>
                        <Typography variant="h4" fontWeight={700} textAlign={"left"}>Antiguedad de Saldo</Typography>
                        <Typography variant="body1" color="text.secondary" textAlign={"left"} sx={{mt:-1}}>Lista de préstamos en mora</Typography>
            </Box>
          </Box>

                  {/* Filters Top Bar */}
                  <Paper sx={{ p: 3, mb: 3 }}>
                    {filterContent}
                  </Paper>

          {/* Metric Cards */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            useFlexGap
            sx={{ mb: 4, flexWrap: "wrap" }}
          >
                    <MetricCard 
                      label="Total Balance" 
                      value={formatNumberWithCommas(totals.total.toFixed(2))} 
                      icon={<AccountBalanceIcon />}
                      delay={0}
                    />
                    <MetricCard 
                      label="0-30 Dias" 
                      value={formatNumberWithCommas(totals.d0_30.toFixed(2))} 
                      icon={<ScheduleIcon />}
                      delay={200}
                    />
                    <MetricCard 
                      label="31-60 Dias" 
                      value={formatNumberWithCommas(totals.d31_60.toFixed(2))} 
                      icon={<ScheduleIcon />}
                      delay={300}
                    />
                    <MetricCard 
                      label="61-90 Dias" 
                      value={formatNumberWithCommas(totals.d61_90.toFixed(2))} 
                      icon={<ScheduleIcon />}
                      delay={400}
                    />
                    <MetricCard 
                      label="+90 Dias" 
                      value={formatNumberWithCommas(totals.d90p.toFixed(2))} 
                      icon={<WarningIcon />}
                      delay={500}
                    />
          </Stack>

          {/* Data Grid */}
          <Paper variant="outlined" sx={{ p: 2 }}>
                    
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              disableRowSelectionOnClick
              pageSizeOptions={[5, 10, 25]}
              initialState={{
                pagination: { paginationModel: { pageSize: 5 } },
              }}
              columnVisibilityModel={columnVisibilityModel}
              sx={{
                "& .ag-col-0-30": { "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 } },
                "& .ag-col-31-60": { "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 } },
                "& .ag-col-61-90": { "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 } },
                "& .ag-col-90p": { "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 } },
              }}
            />
          </Paper>

          {/* Totals footer */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={3}
            sx={{ mt: 2, color: "text.secondary" }}
          >
{/*                    <Typography variant="body2">
                      <strong>Totales:</strong> {currency(totals.total)}
            </Typography>
                    <Typography variant="body2">0–30 días: {currency(totals.d0_30)}</Typography>
                    <Typography variant="body2">31–60 días: {currency(totals.d31_60)}</Typography>
            <Typography variant="body2">61–90: {currency(totals.d61_90)}</Typography>
                    <Typography variant="body2">+90 días: {currency(totals.d90p)}</Typography>
*/}                  </Stack>
                </Box>
              </Box>
            </Container>
          </Stack>
        </Box>
    </Box>
    </AppTheme>
  );
}
